export const buildRakePointNameMap = (rakePoints = []) => {
  const map = {};
  const list = Array.isArray(rakePoints) ? rakePoints : rakePoints?.data || [];

  list.forEach((rakePoint) => {
    if (!rakePoint?.id) return;
    const name = rakePoint.name || rakePoint.rake_point_name || rakePoint.label;
    if (name) map[rakePoint.id] = name;
  });

  return map;
};

const resolveRakePointNameFromMap = (trip, rakePointNameById = {}) => {
  const candidateIds = [
    trip?.origin_rake_point_id,
    trip?.godown_rake_point_id,
    trip?.received_location_rake_point_id,
    trip?.rake_point_id,
  ].filter(Boolean);

  for (const id of candidateIds) {
    if (rakePointNameById[id]) return rakePointNameById[id];
  }

  return null;
};

const isRakeOriginatedSecondary = (trip) =>
  trip?.movement === 'Secondary' &&
  Boolean(trip?.source_rake_movement_id || trip?.origin_rake_point_id);

const isDistributorSecondary = (trip) =>
  trip?.movement === 'Secondary' && !isRakeOriginatedSecondary(trip);

export const getTripRouteSource = (trip, rakePointNameById = {}) => {
  const portName = trip?.port?.port_name || trip?.port?.name || trip?.port_name;
  if (portName) return portName;

  if (isRakeOriginatedSecondary(trip)) {
    return (
      trip?.origin_rake_point?.name ||
      trip?.origin_rake_point_name ||
      resolveRakePointNameFromMap(trip, rakePointNameById) ||
      trip?.rake_point_name ||
      trip?.received_location_name ||
      trip?.godown_rake_point?.name ||
      trip?.rake_point?.name ||
      (trip?.start_latitude != null && trip?.start_longitude != null
        ? `${Number(trip.start_latitude).toFixed(4)}, ${Number(trip.start_longitude).toFixed(4)}`
        : null)
    ) || 'Origin Rake Point';
  }

  if (isDistributorSecondary(trip)) {
    return trip?.distributor?.company_name || 'N/A';
  }

  return 'N/A';
};

export const getTripItemDestination = (trip, item) => {
  if (trip?.movement === 'Secondary') {
    if (isRakeOriginatedSecondary(trip)) {
      return item?.retailer?.company_name || item?.distributor?.company_name || null;
    }
    return item?.retailer?.company_name || null;
  }

  return item?.distributor?.company_name || null;
};

export const getTripRouteDestinations = (trip) => {
  if (trip?.items?.length > 0) {
    return trip.items
      .map((item) => getTripItemDestination(trip, item))
      .filter(Boolean);
  }

  if (trip?.movement === 'Secondary') {
    if (isRakeOriginatedSecondary(trip)) {
      return [trip?.retailer?.company_name || trip?.distributor?.company_name].filter(Boolean);
    }
    return [trip?.retailer?.company_name].filter(Boolean);
  }

  return [trip?.distributor?.company_name].filter(Boolean);
};

export const formatTripRoute = (trip, rakePointNameById = {}) => {
  const source = getTripRouteSource(trip, rakePointNameById);
  const destinations = [...new Set(getTripRouteDestinations(trip))];

  if (!destinations.length) return `${source} → N/A`;
  if (destinations.length === 1) return `${source} → ${destinations[0]}`;
  return `${source} → ${destinations.length} Destinations`;
};

export const getTripProductInfo = (trip) => {
  if (trip?.items?.length > 0) {
    const firstItem = trip.items[0];
    if (firstItem.product_type) {
      return `${firstItem.product_type.name || 'N/A'} (${firstItem.product_quantity || 0} ${firstItem.product_type.unit_of_measure || ''})`;
    }
  }

  if (trip?.product_type) {
    return `${trip.product_type?.name || 'N/A'} (${trip.product_quantity || 0} ${trip.product_type?.unit_of_measure || ''})`;
  }

  return 'N/A';
};

export const mapTripToTableRow = (trip, { includeDriverPhone = false, rakePointNameById = {} } = {}) => {
  const fullName = `${trip?.driver?.user?.first_name || ''} ${trip?.driver?.user?.last_name || ''}`.trim();
  const driverName = fullName || trip?.driver?.user?.username || 'N/A';

  const hasSapDispatch = Boolean(
    trip?.has_sap_dispatch ||
    (Array.isArray(trip?.items) && trip.items.length > 0 && trip.items.some((item) => item?.has_sap_dispatch))
  );

  const row = {
    id: trip.id,
    tripNumber: trip.trip_number || 'N/A',
    driver: includeDriverPhone ? driverName : (trip?.driver?.user?.username || 'N/A'),
    port: trip?.port?.port_name || trip?.port?.name || trip?.port_name || 'N/A',
    truck: trip?.vehicle?.vehicle_number || 'N/A',
    route: formatTripRoute(trip, rakePointNameById),
    product: getTripProductInfo(trip),
    status: trip.status,
    eta: trip.scheduled_end_time ? new Date(trip.scheduled_end_time).toLocaleDateString() : 'N/A',
    createdAt: new Date(trip.created_at).toLocaleString(),
    has_sap_dispatch: hasSapDispatch,
    hasSapDispatch: hasSapDispatch,
    items: trip?.items || [],
    rawTrip: trip,
  };

  if (includeDriverPhone) {
    row.driverPhone = trip?.driver?.user?.phone_number || trip?.driver?.phone_number || '';
  }

  return row;
};
